import { formatNumberWithCommas } from "@/lib/utilities";
import { useEffect, useState } from "react";

import { ExecutionRequestStatus } from "@/lib/interfaces and Enums/ENUMExecutionRequestStatus";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { IBaseAnalyticsCombined } from "@/store/analyticsDataStore";
import { useTheme } from "next-themes";
import { Tooltip } from "react-tooltip";
import { cubeCustomColors } from "../../../../../tailwind.config";
import { filterByInstrumentTypeAll } from "../analyticsHelperFunctions";
import {
  calulateTradesLast7Days,
  filterByCountryName,
  filterByDates,
} from "./ClientSummaryReportHelpers";
import { IClientSummaryFilters } from "./IndexClientSummary";

function calulateActiveTrades(filteredOnCountryData: IBaseAnalyticsCombined[]) {
  const currentESIDs: string[] = [];
  const ActiveTrades = filteredOnCountryData.reduce(
    (acc: number, item: IBaseAnalyticsCombined) => {
      if (
        item.lastAction != ExecutionStrategyStatus.Affirmed &&
        item.lastAction != ExecutionRequestStatus.DealtAway
      ) {
        if (!currentESIDs.includes(item.tradeID)) {
          currentESIDs.push(item.tradeID);
          return acc + 1;
        }
      }
      return acc;
    },
    0,
  );
  return `${ActiveTrades}`;
}

function calulateTotalTrades(filteredOnCountryData: IBaseAnalyticsCombined[]) {
  const currentESIDs: string[] = [];
  const ActiveTrades = filteredOnCountryData.reduce(
    (acc: number, item: IBaseAnalyticsCombined) => {
      if (!currentESIDs.includes(item.tradeID)) {
        acc = acc + 1;
        currentESIDs.push(item.tradeID);
      }

      return acc;
    },
    0,
  );
  return `${ActiveTrades}`;
}

function calulateDV01inBubble(filteredOnCountryData: IBaseAnalyticsCombined[]) {
  const DVO1Value = formatNumberWithCommas(
    filteredOnCountryData.reduce(
      (acc: number, item: IBaseAnalyticsCombined) => {
        return acc + Number(item.DV01);
      },
      0,
    ),
  );
  return `$${DVO1Value}`;
}

function calulateUniqueTradersInBubble(
  filteredOnCountryData: IBaseAnalyticsCombined[],
) {
  const counts: Record<string, number> = {};

  for (const item of filteredOnCountryData) {
    counts[item.RPTraderName] = (counts[item.RPTraderName] || 0) + 1;
  }
  const returnObjectUnfiltered = Object.entries(counts).map(
    ([RPTraderName]) => ({
      RPTraderName,
    }),
  );
  return returnObjectUnfiltered.length;
}

function arrOfUniqueTraders(filteredOnCountryData: IBaseAnalyticsCombined[]) {
  const counts: Record<string, number> = {};

  for (const item of filteredOnCountryData) {
    counts[item.RPTraderName] = (counts[item.RPTraderName] || 0) + 1;
  }
  const returnObjectUnfiltered = Object.entries(counts).map(
    ([RPTraderName]) => ({
      RPTraderName,
    }),
  );
  return returnObjectUnfiltered;
}

function JSXForBubbleToolTip(id: string, data: IBaseAnalyticsCombined[]) {
  switch (id) {
    case "Unique Traders":
      return arrOfUniqueTraders(data).map((item) => item.RPTraderName);
    case "Weekly Trades":
      return calulateTradesLast7Days(data).currentESIDs;
    default:
      return [];
  }
}

function createDataLeftBubbles(
  filteredOnCountryData: IBaseAnalyticsCombined[],
) {
  const bubbleObject = [
    {
      indexPos: 1,
      number: calulateActiveTrades(filteredOnCountryData),
      title: "Active Trades",
    },
    {
      indexPos: 2,
      number: calulateTotalTrades(filteredOnCountryData),
      title: "Trades",
    },
    {
      indexPos: 3,
      number: calulateDV01inBubble(filteredOnCountryData),
      title: "DV01",
    },
  ];
  return bubbleObject;
}

function createDataRightBubbles(
  filteredOnCountryData: IBaseAnalyticsCombined[],
) {
  const bubbleObject = [
    {
      indexPos: 4,
      number: calulateTradesLast7Days(filteredOnCountryData).countLast7Days,
      title: "Weekly Trades",
    },
    {
      indexPos: 5,
      number: calulateUniqueTradersInBubble(filteredOnCountryData),
      title: "Unique Traders",
    },
  ];
  return bubbleObject;
}

export function applySecurityTypeFilters(data: any[], dataFilters: any): any[] {
  if (!Array.isArray(data) || data.length === 0) return [];

  const filteredSecurityData = filterByInstrumentTypeAll(data, dataFilters);

  const filteredDateData = filterByDates(filteredSecurityData, dataFilters);

  // Then filter by date range
  return filteredDateData;
}

const MapSummaryBubbles = ({
  data,
  dataFilters,
}: {
  data: IBaseAnalyticsCombined[];
  dataFilters: IClientSummaryFilters;
}) => {
  function applyClientSummaryFilters(
    data: IBaseAnalyticsCombined[],
    dataFilters: IClientSummaryFilters,
  ): IBaseAnalyticsCombined[] {
    const dateFilteredData = filterByCountryName(data, dataFilters);
    const typeFilteredData = applySecurityTypeFilters(
      dateFilteredData,
      dataFilters,
    );
    return typeFilteredData;
  }

  const { theme } = useTheme();

  const [filteredOnCountryData, setFilteredOnCountryData] = useState(
    applyClientSummaryFilters(data, dataFilters),
  );
  // update filteredData with data change
  useEffect(() => {
    setFilteredOnCountryData(applyClientSummaryFilters(data, dataFilters));
  }, [data, dataFilters]);

  const [tempDataLeft, setTempDataLeft] = useState(
    createDataLeftBubbles(filteredOnCountryData),
  );
  const [tempDataRight, setTempDataRight] = useState(
    createDataRightBubbles(filteredOnCountryData),
  );

  useEffect(() => {
    setTempDataLeft(createDataLeftBubbles(filteredOnCountryData));
    setTempDataRight(createDataRightBubbles(filteredOnCountryData));
  }, [filteredOnCountryData]);

  const bubblesToMap = [
    { id: "leftColMapBubbleGroup", data: tempDataLeft },
    { id: "rightColMapBubbleGroup", data: tempDataRight },
  ];

  // Tooltip Logic
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [toolTipActionedItem, setToolTipActionedItem] = useState("");

  const handleTooltipClick = (id: string, title: string) => {
    if (id == "rightColMapBubbleGroup") {
      setToolTipActionedItem(title);
      setIsTooltipOpen((prev) => !prev); // Toggle tooltip open/close    }
    }
  };

  return (
    <div className="absolute bottom-5 h-[130px] w-[270px]">
      <div className="flex h-full gap-2 overflow-hidden p-1">
        {bubblesToMap.map((group) => (
          <div
            id={group.id}
            className="flex h-full w-1/2 flex-col justify-end gap-2"
            key={group.id}
          >
            {group.data.map((item, index) => {
              return (
                <div
                  {...(group.id === "rightColMapBubbleGroup"
                    ? { "data-tooltip-id": "testOpen" }
                    : {})}
                  key={index}
                  className={`flex h-[30%] w-full items-center rounded-xl bg-[#E0E5EA] p-1 text-left text-xs  dark:bg-gray-700 ${group.id === "rightColMapBubbleGroup" ? "cursor-pointer hover:bg-[#d0d5d9] hover:dark:bg-gray-600" : ""}`}
                  onClick={() => {
                    handleTooltipClick(group.id, item.title);
                  }}
                >
                  <div className="flex p-1">
                    <div className="mr-1 rounded-xl border-2 border-r border-gray-400 "></div>
                    <div className="flex gap-2">
                      <div>{item.number}</div>
                      <div>{item.title}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <Tooltip
        id="testOpen"
        place="top"
        variant="info"
        className="z-90 !opacity-1"
        clickable={true}
        openOnClick={true}
        isOpen={isTooltipOpen} // Controls tooltip visibility
        setIsOpen={setIsTooltipOpen} // Allows tooltip to manage its state
        globalCloseEvents={{
          clickOutsideAnchor: true,
          escape: true,
        }} // Enables closing on outside click and ESC
        border="1px solid gray"
        style={{
          backgroundColor:
            theme == "dark"
              ? cubeCustomColors.cubeBaseDark[700]
              : cubeCustomColors.cubeBase[700],
        }}
      >
        <div className="flex min-h-[140px] w-[270px] flex-col">
          <div className="h-full min-h-[140px] w-full overflow-auto p-2 opacity-100">
            <div className="flex max-h-[300px] flex-col">
              {JSXForBubbleToolTip(
                toolTipActionedItem,
                filteredOnCountryData,
              )?.map((item, index) => (
                <div key={index}>{item}</div>
              ))}
            </div>
          </div>
        </div>
      </Tooltip>
    </div>
  );
};

export default MapSummaryBubbles;
